import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { locationAuditService, LocationAuditLog } from '../services/LocationAuditService';
import { Modal } from '../components/common/Modal';
import { MapPin, AlertTriangle, CheckCircle, XCircle, Clock } from 'lucide-react';

const LocationAuditTrail: React.FC = () => {
  const { user } = useAuth();
  const [auditLogs, setAuditLogs] = useState<LocationAuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LocationAuditLog[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | 'verified' | 'flagged' | 'approved' | 'rejected' | 'unverified'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<LocationAuditLog | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Filter logs based on status
    if (statusFilter === 'all') {
      setFilteredLogs(auditLogs);
    } else {
      setFilteredLogs(auditLogs.filter(log => log.status === statusFilter));
    }
  }, [statusFilter, auditLogs]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);

      // If user is RBT, show only their logs
      // If user is supervisor/admin, show all logs or flagged logs
      let logs: LocationAuditLog[] = [];

      if (user?.role === 'rbt') {
        logs = await locationAuditService.getAuditLogsByRBT(user.id);
      } else if (user?.companyId) {
        // Supervisors/admins see every check-in for the company (not only flagged).
        logs = await locationAuditService.getAuditLogsByCompany(user.companyId);
      }

      setAuditLogs(logs);
      setFilteredLogs(logs);
    } catch (err: any) {
      console.error('Error loading audit logs:', err);
      setError(err.message || 'Failed to load location audit trail');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (log: LocationAuditLog) => {
    if (!user) return;

    try {
      setSubmitting(true);
      await locationAuditService.approveLocation(
        log.id,
        user.name,
        reviewNotes
      );

      setSelectedLog(null);
      setReviewNotes('');
      await loadAuditLogs();
    } catch (err: any) {
      console.error('Error approving location:', err);
      setError(err.message || 'Failed to approve location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (log: LocationAuditLog) => {
    if (!user) return;

    try {
      setSubmitting(true);
      await locationAuditService.rejectLocation(
        log.id,
        user.name,
        reviewNotes
      );

      setSelectedLog(null);
      setReviewNotes('');
      await loadAuditLogs();
    } catch (err: any) {
      console.error('Error rejecting location:', err);
      setError(err.message || 'Failed to reject location');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'flagged':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-blue-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'unverified':
        return <MapPin className="w-5 h-5 text-gray-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">Loading location audit trail...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <MapPin className="w-8 h-8" />
          Location Audit Trail
        </h1>
        <p className="text-gray-600 mt-2">
          Review and manage location verification logs
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Status Filter */}
      <div className="mb-6 flex gap-2">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium ${
            statusFilter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({auditLogs.length})
        </button>
        <button
          onClick={() => setStatusFilter('verified')}
          className={`px-4 py-2 rounded-lg font-medium ${
            statusFilter === 'verified'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Verified ({auditLogs.filter(l => l.status === 'verified').length})
        </button>
        <button
          onClick={() => setStatusFilter('flagged')}
          className={`px-4 py-2 rounded-lg font-medium ${
            statusFilter === 'flagged'
              ? 'bg-yellow-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Flagged ({auditLogs.filter(l => l.status === 'flagged').length})
        </button>
        <button
          onClick={() => setStatusFilter('approved')}
          className={`px-4 py-2 rounded-lg font-medium ${
            statusFilter === 'approved'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Approved ({auditLogs.filter(l => l.status === 'approved').length})
        </button>
        <button
          onClick={() => setStatusFilter('rejected')}
          className={`px-4 py-2 rounded-lg font-medium ${
            statusFilter === 'rejected'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Rejected ({auditLogs.filter(l => l.status === 'rejected').length})
        </button>
        <button
          onClick={() => setStatusFilter('unverified')}
          className={`px-4 py-2 rounded-lg font-medium ${
            statusFilter === 'unverified'
              ? 'bg-gray-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Unverified ({auditLogs.filter(l => l.status === 'unverified').length})
        </button>
      </div>

      {/* Audit Logs Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                RBT
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Appointment Address
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Distance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No location audit logs found
                </td>
              </tr>
            ) : (
              filteredLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        locationAuditService.getStatusColor(log.status)
                      }`}>
                        {log.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {log.rbtName}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {log.appointmentAddress}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {locationAuditService.formatDistance(log.distance)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(log.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {log.status === 'flagged' && user?.role !== 'rbt' && (
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Review
                      </button>
                    )}
                    {log.reviewedBy && (
                      <div className="text-xs text-gray-500 mt-1">
                        Reviewed by: {log.reviewedBy}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Review Modal */}
      <Modal
        isOpen={!!selectedLog}
        onClose={() => {
          setSelectedLog(null);
          setReviewNotes('');
        }}
        title="Review Flagged Location"
        size="md"
      >
        {selectedLog && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Location Verification Required</p>
                <p className="text-sm text-yellow-700 mt-1">
                  This check-in was flagged because it's outside the normal range from the appointment address.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">RBT</label>
                <p className="text-gray-900 mt-1">{selectedLog.rbtName}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Appointment Address</label>
                <p className="text-gray-900 mt-1">{selectedLog.appointmentAddress}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Distance from Appointment</label>
                <p className="text-red-600 font-semibold text-xl mt-1">
                  {locationAuditService.formatDistance(selectedLog.distance)}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Check-in Time</label>
                <p className="text-gray-900 mt-1">{formatDate(selectedLog.createdAt)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">GPS Coordinates</label>
                <div className="text-sm text-gray-600 mt-1 bg-gray-50 p-3 rounded border border-gray-200">
                  <p>Check-in: {selectedLog.checkInLatitude.toFixed(6)}, {selectedLog.checkInLongitude.toFixed(6)}</p>
                  <p className="mt-1">Appointment: {selectedLog.appointmentLatitude.toFixed(6)}, {selectedLog.appointmentLongitude.toFixed(6)}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review Notes
                </label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Add notes about your decision (required for approval/rejection)..."
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => handleApprove(selectedLog)}
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Processing...' : 'Approve Location'}
              </button>
              <button
                onClick={() => handleReject(selectedLog)}
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Processing...' : 'Reject Location'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LocationAuditTrail;
